"use client";
import { useRef } from "react";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import { Container, SectionSubHeading, SectionTitle } from "@/components";
import {
  serviceEvidenceLeft,
  serviceEvidenceRight,
} from "@/data/ServiceEvidenceData";
import { useIsDesktop } from "@/hooks/ui/useIsDesktop";
import { fadeInUp } from "@/lib/animation/variant";
import { ServiceEvidenceBlock } from "@/lib/types";
import { cn } from "@/lib/utils/utils";

const TONE_CLASS: Record<ServiceEvidenceBlock["tone"], string> = {
  plain: "border-white/15 text-white",
  lime: "border-primary bg-primary text-card-foreground",
  dark: "border-background bg-background text-card-foreground",
};

// How far each column travels against the other, in px.
const DRIFT = 130;

const EvidenceBlock = ({ block }: { block: ServiceEvidenceBlock }) => (
  <article
    className={cn(
      "flex flex-col gap-3 rounded-2xl border p-7 md:p-8",
      TONE_CLASS[block.tone],
    )}
  >
    <span
      className={cn(
        "font-mono text-[10px] tracking-[0.24em] uppercase",
        block.tone === "plain" ? "text-primary" : "opacity-60",
      )}
    >
      {block.label}
    </span>

    {block.stat && (
      <p className="text-4xl leading-none font-black tracking-tighter md:text-6xl">
        {block.stat}
      </p>
    )}

    {block.title && (
      <h3 className="text-xl leading-none font-black tracking-tighter uppercase md:text-3xl">
        {block.title}
      </h3>
    )}

    <p
      className={cn(
        "text-sm leading-relaxed md:text-base",
        block.tone === "plain" ? "text-white/60" : "opacity-75",
      )}
    >
      {block.copy}
    </p>
  </article>
);

const ServiceEvidence = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const isDesktop = useIsDesktop();

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  // Springs, not the raw progress: the drift then keeps easing after the wheel
  // stops instead of snapping to the new scroll position.
  const spring = { stiffness: 70, damping: 22, mass: 0.5 };
  const upY = useSpring(
    useTransform(scrollYProgress, [0, 1], [DRIFT, -DRIFT]),
    spring,
  );
  const downY = useSpring(
    useTransform(scrollYProgress, [0, 1], [-DRIFT, DRIFT]),
    spring,
  );

  return (
    <section
      ref={sectionRef}
      className="w-full overflow-hidden rounded-4xl bg-accent-foreground py-20 md:py-28"
    >
      <Container className="flex flex-col gap-8 px-4 lg:px-10">
        <div className="space-y-2">
          <motion.div
            custom={0}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <SectionSubHeading
              subtitle="Why the work holds up"
              className="text-primary"
            />
          </motion.div>

          <motion.div
            custom={1}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <SectionTitle
              title="Two columns, moving in opposite directions."
              className="text-white"
            />
          </motion.div>
        </div>

        <motion.p
          custom={2}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          className="max-w-3xl text-base leading-relaxed font-medium text-white/60 md:text-lg"
        >
          Left column is the decision we made. Right column is what it produced.
          Read them across, because a claim without a receipt is only a claim.
        </motion.p>

        <div className="mt-6 grid gap-5 lg:grid-cols-2 lg:gap-6">
          <motion.div
            style={isDesktop ? { y: upY } : undefined}
            className="grid gap-5 lg:gap-6"
          >
            {serviceEvidenceLeft.map((block) => (
              <EvidenceBlock key={block.label + block.copy} block={block} />
            ))}
          </motion.div>

          <motion.div
            style={isDesktop ? { y: downY } : undefined}
            className="grid gap-5 lg:gap-6"
          >
            {serviceEvidenceRight.map((block) => (
              <EvidenceBlock key={block.label + block.copy} block={block} />
            ))}
          </motion.div>
        </div>
      </Container>
    </section>
  );
};

export default ServiceEvidence;
